import { createFileRoute, Link } from "@tanstack/react-router";
import { DataTable, type DataTableColumnDef } from "@/components/ui/data-table";
import { trpc } from "@/hooks/trpc";
import { useCurrentOperator } from "@/hooks/use-operator";
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  SlimTabs,
  SlimTabsContent,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export const Route = createFileRoute("/batches/")({
  component: RouteComponent,
  validateSearch: (search) => {
    return {
      view: (search.view as string | undefined) || "awaiting",
      q: (search.q as string | undefined) || "",
      status: (search.status as string | undefined) || undefined,
    };
  },
});

// Define the frontend's expected CellType values based on the error message
type FrontendCellTypeId =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "array"
  | "statusBadge";

interface ServiceDataTableColumnDef {
  accessorKey: string;
  header: string;
  id?: string; // often same as accessorKey
  // Use a compatible type here, or map it in the adapter
  cellTypeId?: "string" | "number" | "date" | "boolean" | "datetime";
  enableSorting?: boolean;
  enableHiding?: boolean;
  size?: number;
}

// Helper to adapt backend column defs to frontend
const adaptBackendColumnsToFrontend = (
  backendColumns: ServiceDataTableColumnDef[],
): DataTableColumnDef<Record<string, any>, any>[] => {
  return backendColumns.map((col) => {
    // Make batch name column a link to the batch page
    if (col.accessorKey === "batch_name") {
      return {
        ...col,
        id: col.id ?? col.accessorKey, // Ensure id is set, defaulting to accessorKey
        cellTypeId: undefined,
        cell: ({ row }) => (
          <Link
            to="/batches/$batch_id"
            params={{ batch_id: row.original.batch_id }}
          >
            {row.original.batch_name}
          </Link>
        ),
      };
    }

    let frontendCellTypeId: FrontendCellTypeId | undefined = undefined;
    if (col.cellTypeId) {
      if (col.cellTypeId === "datetime") {
        frontendCellTypeId = "date"; // Map datetime to date, or string if no date renderer handles time
      } else if (
        ["string", "number", "boolean", "date"].includes(col.cellTypeId)
      ) {
        frontendCellTypeId = col.cellTypeId as FrontendCellTypeId;
      }
      // Add other mappings if necessary, e.g., for array or statusBadge if backend sends them
    }

    return {
      ...col,
      id: col.id ?? col.accessorKey, // Ensure id is set, defaulting to accessorKey
      cellTypeId: frontendCellTypeId,
      enableResizing: true,
    };
  });
};

function RouteComponent() {
  const {
    view: viewSearch,
    q: qSearch,
    status: statusSearch,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  const {
    data: batchDetailsResponse,
    isLoading,
    error,
  } = trpc.batches.brewBatches.listFlattenedDetails.useQuery();

  const columns = useMemo(() => {
    if (batchDetailsResponse?.columns) {
      return adaptBackendColumnsToFrontend(batchDetailsResponse.columns);
    }
    return [];
  }, [batchDetailsResponse?.columns]);

  const allData = useMemo(() => {
    return batchDetailsResponse?.data ?? [];
  }, [batchDetailsResponse?.data]);

  const { data: currentOperator } = useCurrentOperator();

  const [view, setView] = useState<"awaiting" | "all" | "mine">(
    (viewSearch as any) || "awaiting",
  );
  const [searchQuery, setSearchQuery] = useState<string>(qSearch || "");
  const [statusFilter, setStatusFilter] = useState<string[]>(
    statusSearch ? (statusSearch.split(",") as string[]) : [],
  );

  // keep URL in sync
  useEffect(() => {
    navigate({
      replace: true,
      search: (prev) => ({
        ...prev,
        view,
        q: searchQuery,
        status: statusFilter.length ? statusFilter.join(",") : undefined,
      }),
    });
  }, [view, searchQuery, statusFilter]);

  const awaitingStatuses = [
    "PENDING_TESTS",
    "TESTS_IN_PROGRESS",
    "PENDING_APPROVAL",
    "ON_HOLD",
  ];

  const filteredAwaiting = useMemo(() => {
    const rows = allData.filter((r: any) =>
      awaitingStatuses.includes(r.clearance_status),
    );
    const needle = searchQuery.trim().toLowerCase();
    const statusSet = new Set(statusFilter);
    return rows.filter((r: any) => {
      if (statusSet.size && !statusSet.has(r.clearance_status)) return false;
      if (!needle) return true;
      const hay = [
        r.batch_name,
        r.harvest_name,
        r.fermentation_name,
        r.fermentation_group_name,
        r.clearance_definition_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [allData, searchQuery, statusFilter]);

  // group by clearance status
  const groupedAwaiting = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const row of filteredAwaiting) {
      const key = row.clearance_status ?? "UNKNOWN";
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }
    // keep a deterministic order matching awaitingStatuses
    const ordered = awaitingStatuses
      .map((key) => [key, groups[key]] as [string, any[]])
      .filter(([, arr]) => arr && arr.length);
    return ordered;
  }, [filteredAwaiting]);

  // "Assigned to Me" list: same UI/logic as Awaiting, but filtered by responsible operator
  const myData = useMemo(() => {
    if (!currentOperator) return [] as any[];
    return allData.filter(
      (r: any) =>
        r.batch_responsible_operator_id === currentOperator.id ||
        r.batch_clearance_responsible_operator_id === currentOperator.id,
    );
  }, [allData, currentOperator]);

  const filteredAwaitingMine = useMemo(() => {
    const rows = myData.filter((r: any) =>
      awaitingStatuses.includes(r.clearance_status),
    );
    const needle = searchQuery.trim().toLowerCase();
    const statusSet = new Set(statusFilter);
    return rows.filter((r: any) => {
      if (statusSet.size && !statusSet.has(r.clearance_status)) return false;
      if (!needle) return true;
      const hay = [
        r.batch_name,
        r.harvest_name,
        r.fermentation_name,
        r.fermentation_group_name,
        r.clearance_definition_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [myData, searchQuery, statusFilter]);

  const groupedAwaitingMine = useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const row of filteredAwaitingMine) {
      const key = row.clearance_status ?? "UNKNOWN";
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }
    const ordered = awaitingStatuses
      .map((key) => [key, groups[key]] as [string, any[]])
      .filter(([, arr]) => arr && arr.length);
    return ordered;
  }, [filteredAwaitingMine]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p>Loading batch details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-destructive">
          Error loading batch details: {error.message}
        </p>
      </div>
    );
  }

  if (!batchDetailsResponse) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p>No batch data available.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 flex flex-col gap-4 flex-1 min-h-0">
      <SlimTabs
        value={view}
        onValueChange={(v) => {
          setView(v as any);
        }}
        className="flex-grow min-h-0 flex flex-col"
      >
        <SlimTabsList>
          {groupedAwaitingMine.length > 0 && (
            <SlimTabsTrigger value="mine">Assigned to Me</SlimTabsTrigger>
          )}
          <SlimTabsTrigger value="awaiting">Awaiting Clearance</SlimTabsTrigger>
          <SlimTabsTrigger value="all">All Batches</SlimTabsTrigger>
        </SlimTabsList>

        <SlimTabsContent
          value="awaiting"
          className="mt-1 grow min-h-0 flex-1 flex flex-col"
        >
          <div className="flex items-center justify-between gap-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="min-w-[260px] mr-6">
                <Input
                  placeholder="Search batches…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <span className="text-xs text-muted-foreground mr-1">
                Clearance:
              </span>
              {awaitingStatuses.map((s) => {
                const active = statusFilter.includes(s);
                return (
                  <button
                    key={s}
                    className={
                      "rounded-full border px-2.5 py-1 text-xs transition-colors inline-flex items-center gap-1 " +
                      (active
                        ? "bg-secondary text-secondary-foreground"
                        : "hover:bg-muted/50")
                    }
                    onClick={() =>
                      setStatusFilter((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s],
                      )
                    }
                  >
                    <ChevronDown className="size-3 text-muted-foreground" />
                    {prettyStatus(s)}
                  </button>
                );
              })}
              {statusFilter.length > 0 && (
                <button
                  className="rounded-full border px-2.5 py-1 text-xs hover:bg-muted/50"
                  onClick={() => setStatusFilter([])}
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-[minmax(220px,2fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(160px,1fr)] items-center gap-3 px-6 text-xs text-muted-foreground">
            <div></div>
            <div>Status</div>
            <div>Harvest</div>
            <div>Fermentation</div>
          </div>
          <AwaitingList groups={groupedAwaiting} />
        </SlimTabsContent>

        <SlimTabsContent
          value="all"
          className="mt-1 grow min-h-0 flex-1 flex flex-col"
        >
          <div className="flex flex-col gap-2 mt-2 h-full min-h-0 grow">
            <DataTable
              columns={columns}
              data={allData}
              searchPlaceholder="Search batches..."
              enableGrouping={false}
              defaultShowFilters={false}
              maxHeight="calc(100vh - 10rem)"
              defaultColumnVisibility={{ batch_id: false }}
              className="h-full"
              enableColumnResizing={true}
              enableColumnVirtualization
            />
          </div>
        </SlimTabsContent>

        <SlimTabsContent
          value="mine"
          className="mt-1 grow min-h-0 flex-1 flex flex-col"
        >
          <div className="flex items-center justify-between gap-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="min-w-[260px] mr-6">
                <Input
                  placeholder="Search batches…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <span className="text-xs text-muted-foreground mr-1">
                Clearance:
              </span>
              {awaitingStatuses.map((s) => {
                const active = statusFilter.includes(s);
                return (
                  <button
                    key={s}
                    className={
                      "rounded-full border px-2.5 py-1 text-xs transition-colors inline-flex items-center gap-1 " +
                      (active
                        ? "bg-secondary text-secondary-foreground"
                        : "hover:bg-muted/50")
                    }
                    onClick={() =>
                      setStatusFilter((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s],
                      )
                    }
                  >
                    <ChevronDown className="size-3 text-muted-foreground" />
                    {prettyStatus(s)}
                  </button>
                );
              })}
              {statusFilter.length > 0 && (
                <button
                  className="rounded-full border px-2.5 py-1 text-xs hover:bg-muted/50"
                  onClick={() => setStatusFilter([])}
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-[minmax(220px,2fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(160px,1fr)] items-center gap-3 px-6 text-xs text-muted-foreground">
            <div></div>
            <div>Status</div>
            <div>Harvest</div>
            <div>Fermentation</div>
          </div>
          <AwaitingList groups={groupedAwaitingMine} />
        </SlimTabsContent>
      </SlimTabs>
    </div>
  );
}

const prettyStatus = (s: string) =>
  s
    .toLowerCase()
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

function AwaitingList({ groups }: { groups: [string, any[]][] }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <motion.div className="space-y-6 select-none" layout>
      {groups.length === 0 && (
        <div className="text-sm text-muted-foreground p-3">
          No batches awaiting clearance.
        </div>
      )}
      <AnimatePresence mode="popLayout">
        {groups.map(([groupTitle, rows]) => {
          const isCollapsed = !!collapsed[groupTitle];
          const prettyGroupTitle = prettyStatus(groupTitle);
          return (
            <motion.div
              key={groupTitle}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <div className="flex items-center justify-between h-8 px-1">
                <div className="flex items-center gap-2">
                  <button
                    className="hover:bg-muted/40 rounded p-1"
                    onClick={() => toggleGroup(groupTitle)}
                  >
                    <ChevronRight
                      className={
                        "size-4 text-muted-foreground transition-transform " +
                        (isCollapsed ? "rotate-0" : "rotate-90")
                      }
                    />
                  </button>
                  <span className="capitalize text-sm text-muted-foreground">
                    {prettyGroupTitle}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">
                    ({rows.length})
                  </span>
                </div>
              </div>
              <motion.div
                layout
                initial={false}
                animate={{
                  height: isCollapsed ? 0 : "auto",
                  opacity: isCollapsed ? 0 : 1,
                }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                style={{ overflow: "hidden" }}
                className="pl-4"
                aria-hidden={isCollapsed}
              >
                <AnimatePresence mode="popLayout">
                  {rows.map((row) => (
                    <motion.div
                      key={row.batch_id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.16, ease: "easeOut" }}
                    >
                      <AwaitingListItem row={row} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
}

function AwaitingListItem({ row }: { row: any }) {
  return (
    <div className="grid grid-cols-[minmax(220px,2fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(160px,1fr)] items-center gap-3 px-5 py-2 rounded hover:bg-muted/40">
      <div className="flex items-center gap-2">
        <Link
          to="/batches/$batch_id"
          params={{ batch_id: row.batch_id }}
          className="hover:underline truncate"
        >
          {row.batch_name}
        </Link>
      </div>
      <div>
        <Badge variant="secondary">{prettyStatus(row.clearance_status)}</Badge>
      </div>
      <div className="truncate">{row.harvest_name ?? "-"}</div>
      <div className="truncate">{row.fermentation_name ?? "-"}</div>
    </div>
  );
}
